import { useRefDataMap } from '@/api/hooks/useRefDataMap'
import type { CrossroadsMappingType } from '@/api/types'
import { BBDTag } from '@gravitate-js/excalibrr'
import { Select } from 'antd'
import { useMemo } from 'react'

interface RefDataSourceIdsSelectProps {
  value: string[]
  onChange: (value: string[]) => void
  sourceSystem: string | undefined
  mappingType: CrossroadsMappingType
  placeholder?: string
  autoFocus?: boolean
  defaultOpen?: boolean
  width?: number
  height?: number
}

const MIN_WIDTH = 300
const MAX_WIDTH = 500

export function RefDataSourceIdsSelect({
  value,
  onChange,
  sourceSystem,
  mappingType,
  placeholder = 'Select values',
  autoFocus = false,
  defaultOpen = false,
  width,
  height,
}: RefDataSourceIdsSelectProps) {
  const { data: entries, isLoading } = useRefDataMap(sourceSystem, mappingType)

  const controlWidth = Math.min(
    Math.max(width ?? MIN_WIDTH, MIN_WIDTH),
    MAX_WIDTH,
  )

  const options = useMemo(() => {
    if (!entries) return []
    return entries.map((entry) => ({
      value: entry.source_id,
      label: entry.display_name,
    }))
  }, [entries])

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Select
        mode='multiple'
        value={value}
        onChange={onChange}
        loading={isLoading}
        placeholder={placeholder}
        showSearch
        allowClear
        autoFocus={autoFocus}
        defaultOpen={defaultOpen}
        style={{ width: controlWidth, height }}
        notFoundContent={isLoading ? 'Loading...' : 'No matches found'}
        filterOption={(input, option) => {
          if (!option) return false
          const searchLower = input.toLowerCase()
          return (
            option.label.toLowerCase().includes(searchLower) ||
            option.value.toLowerCase().includes(searchLower)
          )
        }}
        tagRender={(tagProps) => (
          <BBDTag
            theme2
            style={{ margin: '2px' }}
            closable={tagProps.closable}
            onClose={tagProps.onClose}
          >
            {tagProps.label}
          </BBDTag>
        )}
        optionRender={(option) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <BBDTag theme2 style={{ margin: 0 }}>
              {option.data.label}
            </BBDTag>
            <span style={{ color: 'var(--gray-6)', fontSize: '12px' }}>
              ({option.data.value})
            </span>
          </div>
        )}
        options={options}
        virtual={false}
        placement='bottomLeft'
        popupMatchSelectWidth
        getPopupContainer={() => document.body}
      />
    </div>
  )
}
